/**
 * SlideOver
 * @class
 * @constructor
 * @public
 * @version 1.2.1
 */
class SlideOver extends HTMLElement {
  constructor() {
    super();

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
  }

  /**
   * Getters
   */

  /**
   * @type HTMLDialogElement | null
   */
  get dialog() {
    return this.querySelector('dialog');
  }

  /**
   * @type boolean
   */
  get useModal() {
    return this.getAttribute('modal') !== 'false';
  }

  /**
   * @type string
   */
  get htmlOpenClass() {
    return this.getAttribute('html-open-class') || '';
  }
  
  /**
   * @type boolean
   */
  get defaultOpen() {
    return this.getAttribute('default-open') === 'true' || false;
  }

  /**
   * @type string
   */
  get dialogId() {
    if (!this.dialog?.getAttribute('id')) {
      this.dialog?.setAttribute('id', `id-${Math.random().toString(16).slice(2)}`);
    }
    return this.dialog?.getAttribute('id') || '';
  }

  /**
   * @type HTMLButtonElement | null
   */
  get openButton() {
    // @ts-ignore
    return this.getAttribute('open-button-selector')
      ? // @ts-ignore
      document.querySelector(this.getAttribute('open-button-selector'))
      : this.querySelector(':scope > button');
  }

  /**
   * @type HTMLButtonElement | null
   */
  get closeButton() {
    // @ts-ignore
    return this.getAttribute('close-button-selector')
      ? // @ts-ignore
      this.querySelector(this.getAttribute('close-button-selector'))
      : // @ts-ignore
      this.dialog.querySelector(':scope > button');
  }

  /**
   * connectedCallback
   * Render html/css & attatch event listeners
   */
  connectedCallback() {
    if (!this.openButton?.getAttribute('type')) {
      this.openButton?.setAttribute('type', 'button');
    }
    if (!this.closeButton?.getAttribute('type')) {
      this.closeButton?.setAttribute('type', 'button');
    }
    this.openButton?.setAttribute('aria-controls', this.dialogId);
    this.closeButton?.setAttribute('aria-controls', this.dialogId);

    this.openButton?.addEventListener('click', this.open);
    this.closeButton?.addEventListener('click', this.close);

    this.removePopoverAttributes();

    if (this.defaultOpen) {
      this.open();
    }
  }

  /**
   * disconnectedCallback
   * Remove listeners.
   */
  disconnectedCallback() {
    this.openButton?.removeEventListener('click', this.open);
    this.closeButton?.removeEventListener('click', this.close);
    this.openButton?.removeAttribute('aria-controls');
    this.closeButton?.removeAttribute('aria-controls');

    this.restorePopoverAttributes();
  }

  /**
   * opens the dialog
   */
  open() {
    this.dialog?.classList.remove('closing');
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
    }
    if (this.useModal) {
      this.dialog?.showModal();
    } else {
      this.dialog?.show();
    }
    this.openButton?.setAttribute('aria-expanded', 'true');
    this.closeButton?.setAttribute('aria-expanded', 'true');
    if (this.htmlOpenClass) {
      document.documentElement.classList.add(this.htmlOpenClass);
    }
    this.inertElements(true);
  }

  /**
   * closes the dialog
   */
  close() {
    this.dialog?.classList.add('closing');
    this.closeTimeout = setTimeout(() => {
      this.dialog?.close();
      this.openButton?.setAttribute('aria-expanded', 'false');
      this.closeButton?.setAttribute('aria-expanded', 'false');
      if (this.htmlOpenClass) {
        document.documentElement.classList.remove(this.htmlOpenClass);
      }
    }, 90);

    this.inertElements(false);
  }

  /**
   * Removes popover attributes to progressive enhance
   * the dialog with javascript.
   */
  removePopoverAttributes() {
    if (this.openButton) {
      this.openButton.dataset.popovertarget = this.openButton.getAttribute('popovertarget') || undefined;
      this.openButton.removeAttribute('popovertarget');
    }
    if (this.closeButton) {
      this.closeButton.dataset.popovertarget = this.closeButton.getAttribute('popovertarget') || undefined;
      this.closeButton.removeAttribute('popovertarget');
      this.closeButton.removeAttribute('popovertargetaction');
    }
    this.dialog?.removeAttribute('popover');
  }

  /**
   * Restores the popover attributes
   */
  restorePopoverAttributes() {
    if (this.openButton) {
      this.openButton.setAttribute('popovertarget', this.openButton.dataset.popovertarget || '');
      delete this.openButton.dataset.popovertarget;
    }
    if (this.closeButton) {
      this.closeButton.setAttribute('popovertarget', this.closeButton.dataset.popovertarget || '');
      delete this.closeButton.dataset.popovertarget;
      this.closeButton.setAttribute('popovertargetaction', 'hide');
    }
    this.dialog?.setAttribute('popover', '');
  }

  /**
   * @param {boolean} add
   */
  inertElements(add) {
    if (this.getAttribute('inert-elements')) {
      this.getAttribute('inert-elements')
        ?.split(',')
        .map((selector) => document.querySelector(selector))
        .forEach((el) => {
          if (add) {
            el?.setAttribute('inert', '');
          } else {
            el?.removeAttribute('inert');
          }
        });
    }
  }
}

if (customElements.get('slide-over') === undefined) {
  window.customElements.define('slide-over', SlideOver);
}
